import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputBase,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRoundIcon, ListCheckIcon, PlusIcon, UserRoundPlusIcon } from 'lucide-react';
import { ChangeEvent, useRef, useState } from 'react';
import { useRoleApi } from '../../services/api/useRole.api.ts';
import { useUserApi } from '../../services/api/useUser.api.ts';
import { Invitation } from '../../types/Invitation.type';
import { Role } from '../../types/Role/Role.type.ts';

const UserInviteDataWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  marginY: '16px',
});

const DialogTitleWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '36px',
});

function InviteMember() {
  const queryClient = useQueryClient();
  const { getRoles } = useRoleApi();
  const { sendInvitations, getUserInfo } = useUserApi();

  const [emails, setEmails] = useState(new Set());
  const emailsInputRef = useRef(null);
  const [emailsInputValue, setEmailsInputValue] = useState('');
  const [duplicateEmails, setDuplicateEmails] = useState<string[]>([]);
  const [emailInputErrorMessage, setEmailInputErrorMessage] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<Record<string, number>>({});
  const [dialogVisible, setDialogVisible] = useState(false);

  const { data: user } = useQuery({
    queryKey: ['userInfo'],
    queryFn: getUserInfo,
  });
  const team_id = user?.team?.id;

  const {
    mutate: invitationsMutate,
    data: invitationsResponse,
    isSuccess: isInvitationSent,
    isPending: isInvitationPending,
    reset: resetInvitationState,
  } = useMutation({
    mutationKey: ['invitations'],
    mutationFn: sendInvitations,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'], exact: false });
    },
  });

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  });

  const emailsArray: string[] = Array.from(emails) as string[];

  const handleEmailFieldInputChange = (_: ChangeEvent<unknown>, newInputValue: string) => {
    setEmailsInputValue(newInputValue);
  };

  const handleRoleChange = (email: string, role_Id: number) => {
    setUserRoles({ ...userRoles, [email]: role_Id });
  };

  const openInviteDialog = () => {
    setDialogVisible(true);
  };

  const closeInviteDialog = () => {
    setDialogVisible(false);
  };

  const validateEmailInput = () => {
    if (emailsInputValue) {
      const validEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(emailsInputValue);
      if (validEmail && !emails.has(emailsInputValue)) {
        setEmails(new Set(emails.add(emailsInputValue)));
        setUserRoles({ ...userRoles, [emailsInputValue]: -2 });
        setEmailInputErrorMessage(null);
        setDuplicateEmails([]);
      } else if (!validEmail) {
        setEmailInputErrorMessage('Invalid email address: ' + emailsInputValue);
        setDuplicateEmails([]);
      } else {
        setEmailInputErrorMessage(emailsInputValue + ' is already added.');
        setDuplicateEmails([]);
      }
      setEmailsInputValue('');
    }
  };

  const handleEmailKeyActions = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      validateEmailInput();
      event.preventDefault();
    } else if (event.key === 'Backspace' && emailsInputValue === '' && emails.size > 0) {
      const lastEmail = emailsArray[emailsArray.length - 1];
      setEmails(new Set(emailsArray.slice(0, -1)));
      setEmailsInputValue(lastEmail as string);
      event.preventDefault();
    }
  };

  const deleteEmailFromList = (emailToDelete: string) => {
    const newEmails = new Set(emails);
    newEmails.delete(emailToDelete);
    setEmails(newEmails);
    setDuplicateEmails(duplicateEmails.filter((email) => email !== emailToDelete));
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const pastedText = event.clipboardData.getData('text');
    const pastedEmails = pastedText.split(/[,\s]+/).filter((email) => email !== '');
    const validPastedEmails = pastedEmails.filter((email) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email));

    const uniqueEmailsToAdd: string[] = [];
    const duplicates: string[] = [];

    validPastedEmails.forEach((email) => {
      if (!emails.has(email)) {
        uniqueEmailsToAdd.push(email);
      } else {
        duplicates.push(email);
      }
    });

    const updatedEmails = new Set([...Array.from(emails), ...uniqueEmailsToAdd]);
    setEmails(updatedEmails);

    if (duplicates.length > 0) {
      setDuplicateEmails([...duplicateEmails, ...duplicates]);
      setEmailInputErrorMessage(null);
    } else {
      setDuplicateEmails([]);
      setEmailInputErrorMessage(null);
    }
    event.preventDefault();
  };

  const handleSendInvites = async () => {
    const invites: Invitation[] = Array.from(emails).map((email) => ({
      email: email as string,
      role_id: userRoles[email as string],
      team_id: team_id as number,
    }));

    invitationsMutate(invites);
  };

  const finishInvite = () => {
    setEmails(new Set());
    setUserRoles({});
    setDialogVisible(false);
    resetInvitationState();
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: '440px', maxWidth: '440px' }}>
          <Autocomplete
            multiple
            freeSolo
            inputValue={emailsInputValue}
            onInputChange={handleEmailFieldInputChange}
            options={[]}
            slotProps={{
              popper: {
                sx: {
                  visibility: 'hidden',
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                onBlur={validateEmailInput}
                onKeyDown={handleEmailKeyActions}
                onPaste={handlePaste}
                inputRef={emailsInputRef}
                label='Invite users'
                placeholder='Enter email address'
                slotProps={{
                  ...params.slotProps,

                  input: {
                    ...params.slotProps.input,
                    sx: { fontSize: '14px', minWidth: 0 },
                    startAdornment: (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          overflowY: 'auto',
                          maxHeight: '80px',
                          padding: '4px',
                        }}
                      >
                        {emailsArray.map((email) => (
                          <Chip
                            key={email as string}
                            label={email as string}
                            onDelete={() => deleteEmailFromList(email as string)}
                            sx={{ margin: '2px', fontSize: '12px' }}
                          />
                        ))}
                      </Box>
                    ),
                  },

                  inputLabel: { sx: { fontSize: '14px' } },
                }}
              />
            )}
          />
        </Box>

        <Button
          disabled={emailsArray.length === 0}
          onClick={openInviteDialog}
          variant='contained'
          color='primary'
          sx={{ fontSize: '14px', fontWeight: 'semi-bold', whiteSpace: 'nowrap' }}
        >
          <PlusIcon size='20px' style={{ marginRight: '8px' }} />
          Invite users
        </Button>
        <Dialog open={dialogVisible} onClose={closeInviteDialog} fullWidth>
          <DialogContent>
            {isInvitationSent ? (
              <>
                <DialogTitleWrapper>
                  <ListCheckIcon size={62} color='#132B50' />
                  <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>Invites Sent</Typography>
                </DialogTitleWrapper>

                {invitationsResponse.created_users.length > 0 &&
                  invitationsResponse.created_users.map((user: { email: string }) => (
                    <UserInviteDataWrapper key={user.email}>
                      <UserRoundPlusIcon size={'20px'} />
                      <Typography sx={{ fontWeight: 'medium' }} variant='body2'>
                        {user.email}
                      </Typography>
                      <Chip
                        sx={{ marginLeft: 'auto', fontSize: '10px' }}
                        label='Invited'
                        color='success'
                        size='small'
                      />
                    </UserInviteDataWrapper>
                  ))}

                {invitationsResponse.failed_creation_users.length > 0 &&
                  invitationsResponse.failed_creation_users.map((user: { email: string; error_message: string }) => (
                    <UserInviteDataWrapper key={user.email}>
                      <UserRoundPlusIcon size={'20px'} />
                      <Typography sx={{ fontWeight: 'medium' }} variant='body2'>
                        {user.email}
                      </Typography>
                      <Chip sx={{ marginLeft: 'auto', fontSize: '10px' }} label='Failed' color='error' size='small' />
                    </UserInviteDataWrapper>
                  ))}
              </>
            ) : (
              <>
                <DialogTitleWrapper>
                  {isInvitationPending ? (
                    <>
                      <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>Sending Invites</Typography>
                      <CircularProgress size={62} />
                    </>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>Set Permissions</Typography>
                      <KeyRoundIcon size={62} color='#132B50' />
                    </>
                  )}
                </DialogTitleWrapper>
                {emailsArray.map((email) => (
                  <UserInviteDataWrapper key={email}>
                    <UserRoundPlusIcon size={'20px'} />
                    <Typography sx={{ fontWeight: 'medium' }} variant='body2'>
                      {email}
                    </Typography>
                    <Select
                      sx={{
                        marginLeft: 'auto',
                        minWidth: '180px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e0',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                        '&:hover': {
                          borderColor: '#276749',
                        },
                      }}
                      input={<InputBase />}
                      value={userRoles[email] || -2}
                      onChange={(e) => handleRoleChange(email, Number(e.target.value))}
                    >
                      {roles?.map((role: Role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </UserInviteDataWrapper>
                ))}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {isInvitationSent ? (
              <Button onClick={finishInvite} color='primary'>
                Done
              </Button>
            ) : (
              <>
                <Button sx={{ color: '#000' }} onClick={closeInviteDialog}>
                  Cancel
                </Button>
                <Button onClick={handleSendInvites} color='primary'>
                  Send Invites
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
      <Box sx={{ marginTop: '8px' }}>
        {duplicateEmails.length > 0 && (
          <Typography variant='caption' color='error'>
            Duplicate emails removed: {duplicateEmails.join(', ')}
          </Typography>
        )}
        {emailInputErrorMessage && (
          <Typography variant='caption' color='error'>
            {emailInputErrorMessage}
          </Typography>
        )}
      </Box>
    </>
  );
}

export default InviteMember;
